import strings from 'l10n'
import { isDefined } from './common'
import { UserInfo } from 'types/users'
import { isInvoiceStatus } from 'types/invoice'
import { GridSortDirection } from '@mui/x-data-grid'

export function def(value?: string | null, def = '-') {
  return value ?? def
}

export function percentage(value: number = 0) {
  const f = new Intl.NumberFormat(navigatorLocale(), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })
  return f.format(value * 100)
}

export const removeCommas = (value: string | number) => {
  if (typeof value === 'number') {
    return value
  }
  return value.replace(/,/g, '')
}

export function toDecimal(value?: string | number) {
  if (isDefined(value)) {
    const finalValue: number = typeof value == 'string' ? Number(value.replace(/,/g, '.')) : value
    return (finalValue / 100).toFixed(4)
  }
  return null
}

type DateTimeFormatArgs = {
  date?: string | null
  locale?: string
  defValue?: string
  formatOptions?: Intl.DateTimeFormatOptions
}

export function dateTimeFormat(args: DateTimeFormatArgs) {
  const { date, locale = navigatorLocale(), defValue = '-', formatOptions = {} } = args
  const d = date ? new Date(date) : null
  if (d === null) return defValue
  const formatter = new Intl.DateTimeFormat(locale, formatOptions)
  return formatter.format(d)
}

export function utcDateDateFormat(date: string | null) {
  return dateTimeFormat({
    date: date,
    formatOptions: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    },
  })
}

export function utcDateFormat(date: string | null) {
  return dateTimeFormat({
    date: date,
    formatOptions: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    },
  })
}

export function localeDateTimeFormat(date?: string | null, timeZone?: string | undefined) {
  return dateTimeFormat({
    date: date,
    formatOptions: {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: timeZone,
    },
  })
}

export function currencyFormat(amount?: number, currency: string = 'USD', minimumFractionDigits: number = 2) {
  if (amount === null) return '-'
  const f = new Intl.NumberFormat(navigatorLocale(), {
    currencyDisplay: 'symbol',
    currency: currency,
    style: 'currency',
    minimumFractionDigits: minimumFractionDigits,
  })
  return f.format(amount ? amount : 0)
}

export function percentageFormat(value?: number | null) {
  if (isDefined(value)) {
    return percentage(value) + '%'
  }
  return '-'
}

export function megaByteFormat(bytes?: number | null) {
  if (isDefined(bytes)) {
    const megaBytes = bytes / 1_048_576
    const f = new Intl.NumberFormat(navigatorLocale(), {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return f.format(megaBytes) + 'MB'
  }
  return '-'
}

export function boolFormat(val?: boolean | null) {
  if (val === true) return strings.YES
  if (val === false) return strings.NO
  return '-'
}

function navigatorLocale() {
  const locale = navigator.language ?? 'en-US'
  return locale
}

export function addCurrentTime(dateString: string): Date {
  const date = new Date(dateString)
  const currentDate = new Date()
  if (dateString === currentDate.toISOString().split('T')[0]) {
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    const seconds = currentDate.getSeconds()
    date.setHours(hours, minutes, seconds).toLocaleString()
    date.setDate(date.getDate() + 1)
  } else {
    date.setSeconds(date.getSeconds() - 1)
    date.setDate(date.getDate() + 1)
  }
  return date
}

export function setTimeToZero(date: Date) {
  const dateNoTime = date.toISOString().split('T')[0]
  const finalDate = new Date(dateNoTime)
  return finalDate
}

export const formattedOtherDocumentType = (otherDocumentType: string) => {
  const string = otherDocumentType.toLocaleLowerCase()
  const words = string.split(' ')
  const capitalizedWords = words.map((word) => {
    return word[0].toUpperCase() + word.slice(1)
  })
  return capitalizedWords.join(' ')
}

export function formatInvoiceDate(invoiceDate: string) {
  const currentDate = new Date().toISOString().split('T')[1]
  return invoiceDate.concat('T').concat(currentDate)
}

export function isUpdatedInvoiceDate(invoiceDate: string, dataInvoiceDate: string) {
  return invoiceDate.split('T')[0] === dataInvoiceDate ? invoiceDate : formatInvoiceDate(dataInvoiceDate)
}

export const formatPaymentMethod = (type: string) => {
  if (type === 'ACH') return 'ACH'
  if (type === 'SAME_DAY_ACH') return 'Same Day ACH'
  if (type === 'WIRE') return 'Wire'
  if (type === 'FUEL_CARD') return 'Fuel Card'
  if (type === 'CHECK') return 'Check'

  return type
}
export const formatDaysPer = (type?: string) => {
  switch (type) {
    case 'DAYS_365':
      return '365'
    case 'DAYS_360':
      return '360'
    default:
      return ''
  }
}

export const formatUserResponse = (users?: UserInfo[]) => {
  const modifiedData = users?.map((user) => {
    return {
      ...user,
      id: user.id,
      fullName: user.firstName.concat(' ', user.lastName),
      email: user.email,
      role: user.role.name,
      active: user.active,
    }
  })
  return modifiedData
}

export const formattedInvoiceStatus = (type: string) => {
  if (!isInvoiceStatus(type)) return ''

  if (type === 'PENDING') return strings.PENDING.SINGLE
  if (type === 'APPROVED_FOR_FUNDING') return strings.APPROVED_FOR_FUNDING.SINGLE
  if (type === 'FUNDED') return strings.OPEN.SINGLE_2
  if (type === 'PAID') return strings.CLOSE.SINGLE_2
  if (type === 'REJECTED') return strings.REJECT.PLURAL

  return type
}

export const formattedTotalRate = (totalRate: string = '') => {
  return Number(removeCommas(totalRate.replace('$', '')))
}

export const transformDate = (date: string = '') => {
  const datePattern = /^\d{2}\/\d{2}\/\d{2}$/
  const isValidDate = datePattern.test(date)
  const dateArray = date.split('/')
  let newDate = ''

  if (isValidDate) {
    newDate = '20' + dateArray[2] + '-' + dateArray[1] + '-' + dateArray[0]
  } else {
    newDate = dateArray[2] + '-' + dateArray[1] + '-' + dateArray[0]
  }
  return newDate
}

export const formattedDebtorSortValue = (field: string, sort: GridSortDirection) => {
  const atributeNeedsBaseCompanyPrefix = ['address1', 'phone', 'ein', 'companyName']

  let finalField = ''

  if (atributeNeedsBaseCompanyPrefix.includes(field)) {
    finalField += 'baseCompany.'
  }
  if (field === 'companyName') field = 'name'

  finalField += field

  return `${finalField}:${sort?.toUpperCase()}`
}

export const ucFirst = (str: string) => {
  const lowerStr = str.toLowerCase()
  return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1)
}

export const formattedDebtorRequestStatus = (type: string) => {
  if (type === 'ACTIVE') return strings.ACTIVE
  if (type === 'REQUESTED') return strings.REQUESTED
  return type
}

export const fileSizeConverter = (sizeInBytes: number): string => {
  if (sizeInBytes < 0) {
    throw new Error('Size cannot be negative')
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let unitIndex = 0
  let convertedSize = sizeInBytes

  if (sizeInBytes < 1024) {
    return `${convertedSize.toFixed(2)} ${units[unitIndex]}`
  }

  while (convertedSize >= 1024 && unitIndex < units.length - 1) {
    convertedSize /= 1024
    unitIndex++
  }

  if (unitIndex === 2 && convertedSize < 0.01) {
    unitIndex = 1
    convertedSize = sizeInBytes / 1024
  }

  return `${convertedSize.toFixed(2)} ${units[unitIndex]}`
}

export const formattedDocumentType = (documentType: string) => {
  switch (documentType) {
    case 'INVOICE':
      return 'Invoice'
  }
}

export function sanitizeFilename(fileName: string): string {
  const notAllowedCharacters = /[^a-zA-Z0-9-]/g
  return fileName.replace(notAllowedCharacters, '')
}
